import { useMemo } from 'react';

import { getSelectedNavigationItemCount } from 'components/search/FacetAccordion/FacetAccordion.utils';
import { useFacetsContext } from 'components/search/Facets/FacetsProvider';
import { SIZE_PILL_ID, ZETA_CATEGORY_PILL_ID } from 'constants/quickFilter';
import {
  getActiveZetaCategorySection,
  getQuickFilterAttributes,
  getSingleGroupDefaultExpandedSectionIds,
  getSizeDrawerTitle
} from 'helpers/QuickFilterUtils';
import { GENDER_FACET } from 'helpers/SearchUtils';
import type { NavigationItem, QuickFilterPillItem } from 'types/search';

/**
 * Builds the Quick Filter pills from complete `quickFilter`-flagged backend grouping records in navigation order.
 * Items sharing the same `quickFilterParentGroupId` fold and collapse into one pill; every other flagged item becomes its own.
 * Each pill keeps its navigation items verbatim so the drawer reuses the main filter panel's components.
 */
export const buildQuickFilterPills = (navigation: NavigationItem[] = []): QuickFilterPillItem[] => {
  const pillsById = new Map<string, QuickFilterPillItem>();

  for (const item of navigation) {
    const quickFilterAttributes = getQuickFilterAttributes(item);

    if (!quickFilterAttributes) {
      continue;
    }

    const pillId = quickFilterAttributes.quickFilterParentGroupId;
    const pillLabel = quickFilterAttributes.quickFilterParentGroupDisplayName;
    const pill = pillsById.get(pillId);

    // Fold into the pill already under this key (a later size/width item), else open a new one.
    if (pill) {
      pill.sections.push(item);
      pill.selectedCount += getSelectedNavigationItemCount(item);
    } else {
      pillsById.set(pillId, {
        id: pillId,
        label: pillLabel,
        drawerTitle: pillLabel,
        sections: [item],
        selectedCount: getSelectedNavigationItemCount(item)
      });
    }
  }

  // The ZC pill's active section depends on its complete section set, so it is resolved after the fold.
  const zetaCategoryPill = pillsById.get(ZETA_CATEGORY_PILL_ID);

  if (zetaCategoryPill) {
    zetaCategoryPill.activeSectionId = getActiveZetaCategorySection(zetaCategoryPill.sections, zetaCategoryPill.label)?.facetField;
  }

  // The Size pill's title depends on its complete section set, so it is resolved after the fold.
  const sizePill = pillsById.get(SIZE_PILL_ID);

  if (sizePill) {
    sizePill.drawerTitle = getSizeDrawerTitle(sizePill.sections, navigation, sizePill.label);

    // Defaults apply only with a selected Gender, so an ungendered response never applies the defaults.
    const genderFacet = navigation.find(item => item.facetField === GENDER_FACET);
    const isGenderSelected = genderFacet !== undefined && getSelectedNavigationItemCount(genderFacet) > 0;

    sizePill.defaultExpandedSectionIds = isGenderSelected ? getSingleGroupDefaultExpandedSectionIds(sizePill.sections) : [];
  }

  return [...pillsById.values()];
};

export const useQuickFilterPills = (): QuickFilterPillItem[] => {
  const {
    state: { facets }
  } = useFacetsContext();

  return useMemo(() => buildQuickFilterPills(facets), [facets]);
};
